import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import PageHeader, { Page } from "@/components/layout/PageHeader";
import WorkspaceBanners from "@/components/common/WorkspaceBanners";
import { DemoBadge, EmptyState } from "@/components/common/States";
import ProjectCover from "@/components/common/ProjectCover";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useStudio, useLookups } from "@/store/StudioContext";
import { t } from "@/i18n";

const ProjectsPage = () => {
  const { state, dispatch } = useStudio();
  const { assetById } = useLookups();
  const navigate = useNavigate();
  const [toDelete, setToDelete] = useState(null);
  const projects = [...state.projects].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  const createProject = () => {
    const id = `prj_${Date.now().toString(36)}`;
    dispatch({ type: "PROJECT_CREATE", payload: { id, name: t.common.untitled, format: state.onboarding.workspacePrefs?.format || "16:9" } });
    navigate(`/projects/${id}/storyboard`);
  };

  return (
    <Page testId="projects-page">
      <PageHeader title={t.projects.title} right={<button data-testid="projects-new" className="v-btn-primary min-h-[44px] w-full sm:w-auto justify-center px-4 py-2 text-sm font-semibold" onClick={createProject}><Plus size={16} />{t.projects.new}</button>} />
      <WorkspaceBanners showDemo={false} />
      {projects.length === 0 ? (
        <EmptyState title={t.projects.empty} body={t.projects.emptyBody} testId="projects-empty" action={<button className="v-btn-primary v-btn-sm" data-testid="projects-empty-new" onClick={createProject}>{t.projects.new}</button>} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p, i) => (
            <div key={p.id} data-testid={`project-card-${p.id}`} className="group v-panel v-fade-up overflow-hidden transition-[border-color] hover:border-[#4a4a4a]" style={{ animationDelay: `${i * 40}ms` }}>
              <Link to={`/projects/${p.id}/storyboard`} data-testid={`project-open-${p.id}`}><ProjectCover project={p} state={state} assetById={assetById} fit="contain" className="aspect-[16/10] rounded-none border-0 bg-[#121212]" /></Link>
              <div className="flex items-start justify-between gap-2.5 p-3.5 sm:p-4">
                <div className="min-w-0 flex-1">
                  <Link to={`/projects/${p.id}/storyboard`} className="block truncate font-medium text-sm sm:text-base hover:text-[#f5b544]">{p.name}</Link>
                  <div className="mt-0.5 truncate text-[11px] sm:text-xs text-[#a3a3a3]">{t.projects.scenesCount(state.scenes.filter((s) => s.projectId === p.id).length)} · {t.projects.assetsCount(state.assets.filter((a) => a.projectId === p.id).length)} · {t.formats[p.format]}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {p.isDemo && <DemoBadge />}
                  <button
                    data-testid={`project-delete-${p.id}`}
                    onClick={() => setToDelete(p)}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#6b6b6b] hover:bg-[#222] hover:text-[#e5655a] transition-colors"
                    aria-label={t.common.delete}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)} title={toDelete?.name || ""} body={t.projects.deleteConfirm} onConfirm={() => dispatch({ type: "PROJECT_DELETE", payload: { id: toDelete.id } })} />
    </Page>
  );
};

export default ProjectsPage;
